import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  ArrowLeftIcon,
  CalendarIcon,
  SearchIcon,
  XIcon,
  Loader2,
  Search,
  ChevronDown,
  Check,
  ChevronUp,
  ChevronDown as ChevronDownIcon,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Header } from "../../components/Header/Header";
import { ComponentHeader } from "../../components/Header/ComponentHeader";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import DatePicker from "react-datepicker";
import { predictionsApi, productsApi } from "../../lib/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { store } from "../../store";

interface GeocoderResult {
  geometry: {
    location: {
      lat: () => number;
      lng: () => number;
    };
  };
}

interface GeocoderStatus {
  OK: string;
}

declare global {
  interface Window {
    google: {
      maps: {
        Geocoder: new () => {
          geocode: (
            request: { address: string },
            callback: (results: GeocoderResult[] | null, status: string) => void
          ) => void;
        };
        Map: new (
          element: HTMLElement,
          options: {
            center: { lat: number; lng: number };
            zoom: number;
            streetViewControl?: boolean;
            fullscreenControl?: boolean;
            mapTypeControl?: boolean;
          }
        ) => any;
        Marker: new (options: {
          position: { lat: number; lng: number };
          map: any;
        }) => any;
        places: {
          Autocomplete: new (
            input: HTMLInputElement,
            options: {
              types: string[];
              componentRestrictions?: { country: string };
            }
          ) => any;
        };
        ControlPosition: {
          RIGHT_BOTTOM: number;
        };
        event: {
          addListenerOnce: (instance: any, eventName: string, handler: () => void) => void;
        };
      };
    };
    initMap?: () => void;
  }
}

// Add ReadOnlyInput component
const ReadOnlyInput = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  (props, ref) => (
    <input
      {...props}
      ref={ref}
      readOnly
    />
  )
);

export const CreateRecommendation = (): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const countryMatch = location.pathname.match(/^\/([a-z]{2,})\//i);
  const country = countryMatch ? countryMatch[1] : "in";
  const defaultCenter = { lat: 20.5937, lng: 78.9629 }; // India's center coordinates
  const [selectedHerbicide, setSelectedHerbicide] = useState<string>("");
  const [landPrepDate, setLandPrepDate] = useState<string>("");
  const [transplantDate, setTransplantDate] = useState<string>("");
  const [selectedLocation, setSelectedLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [initialLocation, setInitialLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [name, setName] = useState<string>("");
  const [products, setProducts] = useState<{ id: string, name: string, attributes?: { icon?: string } }[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  const autocompleteRef = useRef<any>(null);
  const mapInstanceRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const locationInputRef = useRef<HTMLInputElement>(null);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [isBackNavigation, setIsBackNavigation] = useState(false);
  const [dialogMessage, setDialogMessage] = useState({
    title: '',
    description: ''
  });
  const [productSearchQuery, setProductSearchQuery] = useState<string>("");
  const [locationSearchQuery, setLocationSearchQuery] = useState<string>("");
  const [isSelectOpen, setIsSelectOpen] = useState<boolean>(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isMarkerMoved, setIsMarkerMoved] = useState(false);
  const [isMapLoading, setIsMapLoading] = useState(true);
  const mainContentRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartY, setDragStartY] = useState(0);
  const [scrollButtonPosition, setScrollButtonPosition] = useState({ 
    x: Math.min(window.innerWidth - 80, 300), 
    y: Math.max(100, 80) // Start below header (60px + 20px margin)
  });
  const scrollButtonRef = useRef<HTMLButtonElement>(null);

  const userId = useSelector((state: RootState) => state.user.userId);
  const token = useSelector((state: RootState) => state.user.token);

  const fetchProducts = async () => {
    try {
      if (!token) {
        setError('Authentication required');
        return;
      }
      setIsLoading(true);
      setError(null);
      const response = await productsApi.getProducts();
      if (response && response.data && Array.isArray(response.data)) {
        setProducts(response.data);
      } else {
        setProducts([]);
        setError('Invalid response format');
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      setError('Failed to fetch products. Please try again.');
      setProducts([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    let script: HTMLScriptElement | null = null;
    let isInitialized = false;
    let mapInitTimeout: NodeJS.Timeout;
    let loadAttempts = 0;
    const MAX_LOAD_ATTEMPTS = 3;

    const loadGoogleMapsScript = (): Promise<void> => {
      return new Promise((resolve, reject) => {
        const apiKey = store.getState().user.googleLocationApiKey;
        if (!apiKey) {
          reject(new Error('Google Maps API key not found'));
          return;
        }

        // Remove any existing script
        const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
        if (existingScript) {
          existingScript.remove();
        }

        script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
        script.async = true;
        script.defer = true;

        script.onload = () => {
          console.log('Google Maps script loaded successfully');
          resolve();
        };

        script.onerror = () => {
          reject(new Error('Failed to load Google Maps script'));
        };

        document.head.appendChild(script);
      });
    };

    const waitForMapContainer = (): Promise<HTMLElement> => {
      return new Promise((resolve, reject) => {
        const checkContainer = () => {
          if (!mapRef.current) return false;
          const rect = mapRef.current.getBoundingClientRect();
          return rect.width > 0 && rect.height > 0;
        };

        if (checkContainer() && mapRef.current) {
          resolve(mapRef.current);
          return;
        }

        const observer = new MutationObserver(() => {
          if (checkContainer() && mapRef.current) {
            observer.disconnect();
            resolve(mapRef.current);
          }
        });

        observer.observe(document.body, {
          childList: true,
          subtree: true,
          attributes: true
        });

        // Fallback timeout
        setTimeout(() => {
          observer.disconnect();
          if (mapRef.current && checkContainer()) { // Re-check before resolving
            resolve(mapRef.current);
          } else {
            reject(new Error('Map container not found or not ready within timeout.'));
          }
        }, 5000);
      });
    };

    const initializeMap = async () => {
      if (isInitialized || !mapRef.current || !locationInputRef.current) return;

      try {
        // Wait for the map container to be ready
        await waitForMapContainer();

        if (!window.google || !window.google.maps) {
          await loadGoogleMapsScript();
        }

        isInitialized = true;
        const defaultCenter = { lat: 20.5937, lng: 78.9629 };

        // Initialize map only if it hasn't been initialized
        if (!mapInstanceRef.current) {
          const mapOptions = {
            center: defaultCenter,
            zoom: 5,
            streetViewControl: false,
            fullscreenControl: false,
            mapTypeControl: false
          };

          // Create map instance
          const map = new window.google.maps.Map(mapRef.current, mapOptions);

          // Wait for the map to be ready
          await new Promise<void>((resolve) => {
            window.google.maps.event.addListenerOnce(map, 'idle', () => {
              resolve();
            });
          });

          mapInstanceRef.current = map;

          // Create location button
          const locationButton = createLocationButton(map);

          // Add click listener to map
          map.addListener('click', (e: any) => {
            const clickedLocation = {
              lat: e.latLng.lat(),
              lng: e.latLng.lng()
            };

            if (markerRef.current) {
              markerRef.current.setMap(null);
            }
            markerRef.current = new window.google.maps.Marker({
              position: clickedLocation,
              map: mapInstanceRef.current,
            });

            setSelectedLocation(clickedLocation);
            setIsMarkerMoved(true);
          });

          // Initialize autocomplete
          initializeAutocomplete(map);

          // Get user's location if available
          if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
              (position) => {
                const userLocation = {
                  lat: position.coords.latitude,
                  lng: position.coords.longitude
                };

                setInitialLocation(userLocation);
                setSelectedLocation(userLocation);

                if (mapInstanceRef.current) {
                  mapInstanceRef.current.setCenter(userLocation);
                  mapInstanceRef.current.setZoom(15);

                  if (markerRef.current) {
                    markerRef.current.setMap(null);
                  }
                  markerRef.current = new window.google.maps.Marker({
                    position: userLocation,
                    map: mapInstanceRef.current,
                  });
                }
                setIsMapLoading(false);
              },
              (error) => {
                console.error('Error getting location:', error);
                // Keep default center if geolocation fails
                setIsMapLoading(false);
              }
            );
          } else {
            // If geolocation is not supported, stop loading
            setIsMapLoading(false);
          }
        }
      } catch (error) {
        console.error('Error initializing map:', error);
        isInitialized = false;
        if (loadAttempts < MAX_LOAD_ATTEMPTS) {
          loadAttempts++;
          mapInitTimeout = setTimeout(initializeMap, 1000);
        }
      }
    };

    // Start initialization
    initializeMap();

    return () => {
      if (mapInitTimeout) {
        clearTimeout(mapInitTimeout);
      }
      if (script && script.parentNode) {
        script.parentNode.removeChild(script);
      }
      isInitialized = false;

      // Clean up map instance
      if (markerRef.current) {
        markerRef.current.setMap(null);
        markerRef.current = null;
      }
      if (mapInstanceRef.current) {
        mapInstanceRef.current = null;
      }
    };
  }, []);

  const createLocationButton = (map: any) => {
    const locationButton = document.createElement('button');
    locationButton.className = 'custom-map-control-button';
    locationButton.innerHTML = `
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle cx="12" cy="12" r="6" fill="#111" />
        <circle cx="12" cy="12" r="2.5" fill="#fff" />
        <g stroke="#111" stroke-width="2">
          <circle cx="12" cy="12" r="9" fill="none" />
          <line x1="12" y1="3" x2="12" y2="6" />
          <line x1="12" y1="18" x2="12" y2="21" />
          <line x1="3" y1="12" x2="6" y2="12" />
          <line x1="18" y1="12" x2="21" y2="12" />
        </g>
      </svg>
    `;
    locationButton.title = t('yourLocation');
    locationButton.style.cssText = `
      background-color: white;
      border: none;
      border-radius: 2px;
      box-shadow: 0 1px 4px rgba(0,0,0,0.3);
      cursor: pointer;
      padding: 8px;
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 1;
      pointer-events: auto;
    `;

    const locationButtonContainer = document.createElement('div');
    locationButtonContainer.style.cssText = `
      position: absolute;
      right: 10px;
      bottom: 80px;
      z-index: 1;
    `;
    locationButtonContainer.appendChild(locationButton);

    mapRef.current?.appendChild(locationButtonContainer);

    locationButton.addEventListener('click', () => {
      if (navigator.geolocation) {
        setIsMapLoading(true);
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const userLocation = {
              lat: position.coords.latitude,
              lng: position.coords.longitude
            };
            if (mapInstanceRef.current) {
              mapInstanceRef.current.setCenter(userLocation);
              mapInstanceRef.current.setZoom(15);

              if (markerRef.current) {
                markerRef.current.setMap(null);
              }
              markerRef.current = new window.google.maps.Marker({
                position: userLocation,
                map: mapInstanceRef.current,
              });
              setSelectedLocation(userLocation);
              if (locationInputRef.current) {
                locationInputRef.current.value = '';
              }
              setLocationSearchQuery("");
              setIsMarkerMoved(false);
            }
            setIsMapLoading(false);
          },
          (error) => {
            console.error('Error getting location:', error);
            toast.error(t('unableToGetLocation'));
            setIsMapLoading(false);
          }
        );
      } else {
        toast.error(t('geolocationNotSupported'));
      }
    });

    return locationButton;
  };

  const initializeAutocomplete = (map: any): void => {
    if (!locationInputRef.current) return;

    try {
      const autocomplete = new window.google.maps.places.Autocomplete(locationInputRef.current, {
        types: ['geocode']
      });

      autocompleteRef.current = autocomplete;

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (place.geometry) {
          const location = {
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng()
          };
          map.setCenter(location);
          map.setZoom(15);
          if (markerRef.current) {
            markerRef.current.setMap(null);
          }
          markerRef.current = new window.google.maps.Marker({
            position: location,
            map,
          });
          setSelectedLocation(location);
          setIsMarkerMoved(true);
          if (locationInputRef.current) {
            locationInputRef.current.value = place.formatted_address || '';
            setLocationSearchQuery(place.formatted_address || '');
          }
        }
      });
    } catch (error) {
      console.error('Error initializing autocomplete:', error);
    }
  };

  const handleGoBack = (): void => {
    if (isAnyFieldFilled()) {
      setDialogMessage({
        title: 'Are you sure you want to go back?',
        description: 'Your unsaved data will be lost.'
      });
      setShowDiscardDialog(true);
      setIsBackNavigation(true);
    } else {
      navigate(`/${country}/my-predictions`);
    }
  };

  const handleSubmit = async () => {
    if (!userId) {
      toast.error(t('userNotFound'));
      return;
    }

    if (!landPrepDate || !transplantDate) {
      toast.error(t('pleaseSelectDates'));
      return;
    }

    if (selectedLocation) {
      setIsSubmitting(true);
      const submitData = {
        productId: selectedHerbicide,
        location: {
          latitude: selectedLocation.lat.toString(),
          longitude: selectedLocation.lng.toString()
        },
        attributes: {
          land_preparation_date: landPrepDate,
          transplant_date: transplantDate
        },
        name: name,
        userId: userId,
        cropId: "1"
      };

      try {
        const response = await predictionsApi.createPrediction(submitData);
        if (response && response.data && response.data.id) {
          toast.success(t('predictionCreated'));
          navigate(`/${country}/prediction-details`, { state: { id: response.data.id } });
        } else {
          toast.error(t('invalidResponse'));
        }
      } catch (error: any) {
        if (error.status) {
          const numericStatusCode = Number(error.status);
          if (numericStatusCode === 406) {
            setErrorMessage(error.message || t('errorCreatingPrediction'));
            setShowErrorDialog(true);
          } else {
            toast.error(error.message || t('errorCreatingPrediction'));
          }
        } else {
          toast.error(t('errorCreatingPrediction')); // Fallback for network errors or unhandled exceptions without a specific status code
        }
      } finally {
        setIsSubmitting(false);
      }
    } else {
      toast.error(t('pleaseSelectLocation'));
    }
  };

  const handleReset = () => {
    if (isAnyFieldFilled()) {
      setDialogMessage({
        title: 'Reset Changes?',
        description: 'Are you sure you want to reset?'
      });
      setShowDiscardDialog(true);
      setIsBackNavigation(false);
    } else {
      resetForm();
    }
  };

  const resetForm = () => {
    setSelectedHerbicide("");
    setLandPrepDate("");
    setTransplantDate("");
    setSelectedLocation(null);
    setName("");
    if (locationInputRef.current) {
      locationInputRef.current.value = '';
    }
    if (markerRef.current) {
      markerRef.current.setMap(null);
    }
    const selectTrigger = document.querySelector('[data-value]');
    if (selectTrigger) {
      selectTrigger.setAttribute('data-value', '');
    }
  };

  const handleDiscardChanges = () => {
    setShowDiscardDialog(false);
    if (isBackNavigation) {
      navigate(`/${country}/my-predictions`);
    } else {
      resetForm();
      // Reset map to current user location
      if (navigator.geolocation && mapInstanceRef.current) {
        setIsMapLoading(true);
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const userLocation = {
              lat: position.coords.latitude,
              lng: position.coords.longitude
            };
            
            // Update map center and zoom
            mapInstanceRef.current.setCenter(userLocation);
            mapInstanceRef.current.setZoom(15);
            
            // Update marker
            if (markerRef.current) {
              markerRef.current.setMap(null);
            }
            markerRef.current = new window.google.maps.Marker({
              position: userLocation,
              map: mapInstanceRef.current,
            });
            
            // Update state
            setSelectedLocation(userLocation);
            setInitialLocation(userLocation);
            
            // Clear location input
            if (locationInputRef.current) {
              locationInputRef.current.value = '';
            }
            setLocationSearchQuery("");
            setIsMarkerMoved(false);
            setIsMapLoading(false);
          },
          (error) => {
            console.error('Error getting location:', error);
            // If geolocation fails, reset to default center
            const defaultCenter = { lat: 20.5937, lng: 78.9629 };
            mapInstanceRef.current.setCenter(defaultCenter);
            mapInstanceRef.current.setZoom(5);
            
            if (markerRef.current) {
              markerRef.current.setMap(null);
            }
            setSelectedLocation(null);
            setInitialLocation(null);
            
            if (locationInputRef.current) {
              locationInputRef.current.value = '';
            }
            setLocationSearchQuery("");
            setIsMarkerMoved(false);
            setIsMapLoading(false);
          }
        );
      }
    }
  };

  const handleCancelDiscard = () => {
    setShowDiscardDialog(false);
  };

  const isAnyFieldFilled = (): boolean => {
    return selectedHerbicide !== "" ||
           landPrepDate !== "" ||
           transplantDate !== "" ||
           selectedLocation !== null ||
           name !== "";
  };

  const filteredProducts = products.filter((product) =>
    product.name.toLowerCase().includes(productSearchQuery.toLowerCase())
  );

  return (
    <div>
      <Header className="hidden sm:flex"/>
      <ComponentHeader name={t('createRecommendation')} handleClick={handleGoBack}/>
      <div className="bg-[#fcfcfc] flex flex-row justify-center w-full">
        <div className="bg-[#fcfcfc] w-full relative pr-[4px] sm:pr-0">
          <nav className="fixed left-0 top-[60px] hidden sm:flex flex-col w-[100px] h-[calc(100vh-60px)] items-start gap-5 p-2.5 bg-primary-new500">
            <div
              className="cursor-pointer relative w-20 h-20 bg-[#014224] rounded-[10px] flex items-center justify-center"
              onClick={() => navigate(`/${country}/my-predictions`)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  navigate(`/${country}/my-predictions`);
                }
              }}
              role="button"
              tabIndex={0}
            >
              <img
                className="w-11 h-11"
                alt={t('iconPrediction')}
                src="https://c.animaapp.com/m9qnee32rCPMMo/img/icon-prediction.svg"
              />
            </div>
          </nav>

          <div
            ref={mainContentRef}
            className="sm:ml-[100px] sm:pt-5 sm:w-[calc(100%-100px)] h-[calc(100dvh-60px)] overflow-auto custom-mobile-scrollbar"
          >
            <div className="">
              <div className="hidden sm:inline-flex h-11 items-center gap-[973px] px-6 py-4">
                <div className="flex w-full items-center gap-6 relative">
                  <div className="flex items-center gap-3.5 relative flex-1 grow">
                    <ArrowLeftIcon className="w-5 h-5 cursor-pointer" onClick={handleGoBack} data-testid="arrow-left-icon" />
                    <h1 className="relative flex-1 mt-[-1.00px] font-bold text-[#14151c] text-lg tracking-[-0.54px] leading-[22px]">
                      {t('createRecommendation')}
                    </h1>
                  </div>
                </div>
              </div>

              <Card className="rounded-none sm:rounded-xl border-none sm:border sm:mx-6 sm:shadow-[0px_0px_10px_2px_#0000001a]">
                <CardContent className="flex flex-col lg:flex-row items-start h-auto lg:h-[calc(100dvh-235px)] lg:overflow-auto p-[20px] sm:p-6 gap-6">
                  <div className="inline-flex flex-col h-auto w-full lg:w-auto items-start gap-2.5 sm:p-2.5 bg-neutral-50 rounded-md">
                    <div className="flex flex-col w-full sm:w-[338px] items-start gap-2 relative">
                      <label className="self-stretch font-regular-body-2 text-[#14151c] text-[length:var(--regular-body-2-font-size)] tracking-[var(--regular-body-2-letter-spacing)] leading-[var(--regular-body-2-line-height)]">
                        {t('selectProduct')}
                      </label>
                      <div className="relative w-full sm:min-w-[180px]">
                        <button
                          type="button"
                          className="flex h-[50px] w-full items-center justify-between whitespace-nowrap rounded-md border border-[#c2c7d0] bg-white px-3 py-2 text-sm shadow-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                          onClick={() => setIsSelectOpen(!isSelectOpen)}
                          data-testid="product-select"
                        >
                          <div className="flex items-center gap-2">
                            {selectedHerbicide && products.find(p => p.id === selectedHerbicide)?.attributes?.icon && (
                              <img
                                src={products.find(p => p.id === selectedHerbicide)?.attributes?.icon}
                                alt=""
                                className="w-5 h-5 object-contain"
                              />
                            )}
                            <span className="block truncate">
                              {selectedHerbicide
                                ? products.find(p => p.id === selectedHerbicide)?.name || t('selectHerbicide')
                                : t('selectHerbicide')
                              }
                            </span>
                          </div>
                          <ChevronDown className="h-4 w-4 opacity-50" />
                        </button>

                        {isSelectOpen && (
                          <div className="absolute z-50 w-full mt-1 bg-white border border-[#c2c7d0] rounded-md shadow-lg max-h-60 overflow-hidden">
                            {/* Search Input */}
                            <div className="sticky top-0 bg-white p-2 border-b border-[#c2c7d0]">
                              <div className="relative">
                                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
                                <Input
                                  className="pl-8 h-9"
                                  placeholder={t('searchProducts')}
                                  value={productSearchQuery}
                                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setProductSearchQuery(e.target.value)}
                                  autoComplete="off"
                                  autoFocus
                                />
                              </div>
                            </div>

                            {/* Options */}
                            <div className="max-h-48 overflow-y-auto">
                              {isLoading ? (
                                <div className="flex items-center justify-center p-4">
                                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                  {t('loading')}
                                </div>
                              ) : error ? (
                                <div className="flex items-center justify-center p-4 text-red-500">
                                  {error}
                                </div>
                              ) : filteredProducts.length === 0 ? (
                                <div className="flex items-center justify-center p-4 text-gray-500">
                                  {productSearchQuery ? t('noProductsFound') : t('noProductsAvailable')}
                                </div>
                              ) : (
                                filteredProducts.map((product) => (
                                  <button
                                    key={`product-${product.id}`}
                                    type="button"
                                    className={`w-full px-3 py-2 text-left hover:bg-gray-100 flex items-center justify-between ${
                                      selectedHerbicide === product.id ? 'bg-gray-50' : ''
                                    }`}
                                    onClick={() => {
                                      setSelectedHerbicide(product.id);
                                      setProductSearchQuery("");
                                      setIsSelectOpen(false);
                                    }}
                                  >
                                    <div className="flex items-center gap-2">
                                      {product.attributes?.icon && (
                                        <img
                                          src={product.attributes.icon}
                                          alt=""
                                          className="w-5 h-5 object-contain"
                                        />
                                      )}
                                      <span>{product.name}</span>
                                    </div>
                                    {selectedHerbicide === product.id && (
                                      <Check className="h-4 w-4 text-green-600" />
                                    )}
                                  </button>
                                ))
                              )}
                            </div>
                          </div>
                        )}

                        {/* Overlay to close dropdown when clicking outside */}
                        {isSelectOpen && (
                          <div
                            className="fixed inset-0 z-40"
                            onClick={() => {
                              setIsSelectOpen(false);
                              setProductSearchQuery("");
                            }}
                            onKeyDown={(e: React.KeyboardEvent) => {
                              if (e.key === 'Escape') {
                                setIsSelectOpen(false);
                                setProductSearchQuery("");
                              }
                            }}
                            role="button"
                            tabIndex={0}
                            aria-label="Close product dropdown"
                          />
                        )}
                      </div>
                    </div>

                    <div className="flex flex-col w-full sm:w-[338px] items-start gap-2 relative">
                      <label className="self-stretch font-regular-body-2 text-[#14151c] text-[length:var(--regular-body-2-font-size)] tracking-[var(--regular-body-2-letter-spacing)] leading-[var(--regular-body-2-line-height)]">
                        {t('landPreparationDate')}
                      </label>
                      <div className="relative w-full">
                        <DatePicker
                          selected={landPrepDate ? new Date(landPrepDate) : null}
                          onChange={(date: Date | null) => {
                            if (date) {
                              const year = date.getFullYear();
                              const month = String(date.getMonth() + 1).padStart(2, '0');
                              const day = String(date.getDate()).padStart(2, '0');
                              setLandPrepDate(`${year}-${month}-${day}`);
                              setTransplantDate("");
                            } else {
                              setLandPrepDate("");
                              setTransplantDate("");
                            }
                          }}
                          minDate={new Date(Date.now() - 6 * 24 * 60 * 60 * 1000)}
                          maxDate={new Date(Date.now() + 10 * 24 * 60 * 60 * 1000)}
                          showMonthDropdown
                          showYearDropdown
                          dropdownMode="select"
                          dateFormat="dd/MM/yyyy"
                          placeholderText="dd/mm/yyyy"
                          isClearable
                          popperClassName="z-[9999]"
                          popperPlacement="bottom-start"
                          portalId="datepicker-portal"
                          customInput={
                            <ReadOnlyInput
                              className="h-[50px] min-w-full border border-[#c2c7d0] shadow-[0px_0px_4px_#0000001a] rounded-md px-3 cursor-pointer"
                              style={{ caretColor: 'transparent' }}
                            />
                          }
                        />
                        <CalendarIcon className="absolute right-6 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
                      </div>
                    </div>

                    <div className="flex flex-col w-full sm:w-[338px] items-start gap-2 relative">
                      <label className="self-stretch font-regular-body-2 text-[#14151c] text-[length:var(--regular-body-2-font-size)] tracking-[var(--regular-body-2-letter-spacing)] leading-[var(--regular-body-2-line-height)]">
                        {t('transplantDate')}
                      </label>
                      <div className="relative w-full">
                        <DatePicker
                          selected={transplantDate ? new Date(transplantDate) : null}
                          onChange={(date: Date | null) => {
                            if (date) {
                              const year = date.getFullYear();
                              const month = String(date.getMonth() + 1).padStart(2, '0');
                              const day = String(date.getDate()).padStart(2, '0');
                              setTransplantDate(`${year}-${month}-${day}`);
                            } else {
                              setTransplantDate("");
                            }
                          }}
                          minDate={landPrepDate ? new Date(landPrepDate) : undefined}
                          maxDate={landPrepDate ? new Date(new Date(landPrepDate).getTime() + 2 * 24 * 60 * 60 * 1000) : undefined}
                          showMonthDropdown
                          showYearDropdown
                          dropdownMode="select"
                          dateFormat="dd/MM/yyyy"
                          placeholderText="dd/mm/yyyy"
                          isClearable
                          disabled={!landPrepDate}
                          popperClassName="z-[9999]"
                          popperPlacement="bottom-start"
                          portalId="datepicker-portal"
                          customInput={
                            <ReadOnlyInput
                              className={`h-[50px] min-w-full border border-[#c2c7d0] shadow-[0px_0px_4px_#0000001a] rounded-md px-3 ${!landPrepDate ? 'bg-gray-100 cursor-not-allowed' : 'cursor-pointer'}`}
                              style={{ caretColor: 'transparent' }}
                            />
                          }
                        />
                        <CalendarIcon className="absolute right-6 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
                      </div>
                      {landPrepDate && transplantDate &&
                        (new Date(transplantDate).getTime() - new Date(landPrepDate).getTime()) > 7 * 24 * 60 * 60 * 1000 && (
                          <p className="text-red-500 text-sm mt-1">
                            {t('gapExceedsWarning')}
                          </p>
                        )}
                    </div>

                    <div className="flex flex-col w-full sm:w-[338px] items-start gap-2 relative">
                      <label className="self-stretch font-regular-body-2 text-[#14151c] text-[length:var(--regular-body-2-font-size)] tracking-[var(--regular-body-2-letter-spacing)] leading-[var(--regular-body-2-line-height)]">
                        {t('fieldNameOpt')}
                      </label>
                      <Input
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          const value = e.target.value;
                          if (value.length <= 50) {
                            setName(value);
                          }
                        }}
                        className="h-[50px] min-w-full border border-[#c2c7d0] shadow-[0px_0px_4px_#0000001a] rounded-md px-3"
                        placeholder={t('enterFieldName')}
                        maxLength={50}
                        value={name}
                      />
                    </div>
                  </div>

                  <div className="relative flex-1 self-stretch bg-[#acacac] rounded overflow-hidden">
                    <div className="relative w-full h-[582px] lg:h-full">
                      {/* Map Loading Overlay */}
                      {isMapLoading && (
                        <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center z-20">
                          <div className="flex flex-col items-center gap-3">
                            <Loader2 className="h-8 w-8 animate-spin text-primary-new500" />
                            <p className="text-sm text-gray-600">{t('findingYourLocation')}</p>
                          </div>
                        </div>
                      )}
                      
                      <div className="absolute w-[382px] h-[130px] top-0 left-0 z-10">
                        <div className="absolute w-auto sm:w-[362px] h-[50px] top-5 left-5 shadow-[0px_4px_4px_#00000040]">
                          <div className="relative w-auto sm:w-[360px] h-[50px] bg-white rounded-lg flex items-center px-3">
                            <SearchIcon className="w-[18px] h-[17px] mr-2" />
                            <Input
                              ref={locationInputRef}
                              className="flex-1 border-none pl-2 text-neutral-grayneutral-100 text-sm tracking-[-0.07px] leading-5"
                              placeholder={t('searchLocation')}
                              value={locationSearchQuery}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                setLocationSearchQuery(e.target.value);
                                if (!e.target.value) {
                                  setSelectedLocation(null);
                                  if (markerRef.current) {
                                    markerRef.current.setMap(null);
                                    markerRef.current = null;
                                  }
                                  if (mapInstanceRef.current) {
                                    mapInstanceRef.current.setCenter(defaultCenter);
                                    mapInstanceRef.current.setZoom(5);
                                  }
                                  setIsMarkerMoved(true);
                                }
                              }}
                              onFocus={() => {
                                if (!locationSearchQuery) {
                                  setIsMarkerMoved(true);
                                }
                              }}
                            />
                            <XIcon
                              className="w-[25px] h-6 cursor-pointer"
                              data-testid="clear-location"
                              onClick={() => {
                                if (locationInputRef.current) {
                                  locationInputRef.current.value = '';
                                  setSelectedLocation(null);
                                  if (markerRef.current) {
                                    markerRef.current.setMap(null);
                                    markerRef.current = null;
                                  }
                                  if (mapInstanceRef.current) {
                                    mapInstanceRef.current.setCenter(defaultCenter);
                                    mapInstanceRef.current.setZoom(5);
                                  }
                                }
                                setLocationSearchQuery("");
                                setIsMarkerMoved(true);
                              }}
                            />
                          </div>
                          {(!locationSearchQuery && isMarkerMoved) && (
                            <div className="w-full flex justify-center mt-2">
                              <button
                                type="button"
                                className="w-full flex items-center gap-2 bg-white rounded-lg shadow border border-[#c2c7d0] px-3 py-2 text-primary-new500 hover:bg-gray-50 transition focus:outline-none focus:ring-2 focus:ring-primary-new500"
                                style={{ maxWidth: 340 }}
                                onClick={() => {
                                  if (navigator.geolocation) {
                                    setIsMapLoading(true);
                                    navigator.geolocation.getCurrentPosition(
                                      (position) => {
                                        const userLocation = {
                                          lat: position.coords.latitude,
                                          lng: position.coords.longitude
                                        };
                                        if (mapInstanceRef.current) {
                                          mapInstanceRef.current.setCenter(userLocation);
                                          mapInstanceRef.current.setZoom(15);
                                        }
                                        if (markerRef.current) {
                                          markerRef.current.setMap(null);
                                        }
                                        markerRef.current = new window.google.maps.Marker({
                                          position: userLocation,
                                          map: mapInstanceRef.current,
                                        });
                                        setSelectedLocation(userLocation);
                                        if (locationInputRef.current) {
                                          locationInputRef.current.value = '';
                                        }
                                        setLocationSearchQuery("");
                                        setIsMarkerMoved(true);
                                        setIsMapLoading(false);
                                      },
                                      (error) => {
                                        console.error('Error getting location:', error);
                                        toast.error(t('unableToGetLocation'));
                                        setIsMapLoading(false);
                                      }
                                    );
                                  } else {
                                    toast.error(t('geolocationNotSupported'));
                                  }
                                }}
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="24"
                                  height="24"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                >
                                  <circle cx="12" cy="12" r="6" fill="#111" />
                                  <circle cx="12" cy="12" r="2.5" fill="#fff" />
                                  <g stroke="#111" stroke-width="2">
                                    <circle cx="12" cy="12" r="9" fill="none" />
                                    <line x1="12" y1="3" x2="12" y2="6" />
                                    <line x1="12" y1="18" x2="12" y2="21" />
                                    <line x1="3" y1="12" x2="6" y2="12" />
                                    <line x1="18" y1="12" x2="21" y2="12" />
                                  </g>
                                </svg>
                                {t('yourLocation')}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                      <div
                        ref={mapRef}
                        className="w-full h-full"
                        style={{ position: 'relative', minHeight: '400px', visibility: 'visible' }}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="flex justify-end gap-2.5 px-6 py-[18px] mt-6 bg-[#fcfcfc] border-t border-[#c8c5c5]">
                <Button
                  variant="outline"
                  className="w-[154px] h-[46px] bg-white rounded-[100px] border border-solid border-[#0c612c] text-primary-new500"
                  onClick={handleReset}
                  disabled={isSubmitting}
                >
                  {t('reset')}
                </Button>
                <Button
                  className="w-[154px] h-[46px] bg-primary-new500 rounded-[100px] text-white"
                  onClick={handleSubmit}
                  disabled={!selectedHerbicide || !landPrepDate || !transplantDate || isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('submitting')}
                    </>
                  ) : (
                    t('submit')
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog
        open={showErrorDialog}
        onOpenChange={setShowErrorDialog}
      >
        <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('error')}</DialogTitle>
              <DialogDescription>{errorMessage}</DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
              <Button
                onClick={() => {
                  setShowErrorDialog(false);
                  navigate(`/${country}/my-predictions`);
                }}
              >
                {t('ok')}
              </Button>
            </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showDiscardDialog} onOpenChange={setShowDiscardDialog}>
        <DialogContent>
            <DialogHeader>
              <DialogTitle>{dialogMessage.title}</DialogTitle>
              <DialogDescription>{dialogMessage.description}</DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
              <Button variant="outline" onClick={handleCancelDiscard}>
                {t('no')}
              </Button>
              <Button variant="destructive" onClick={handleDiscardChanges}>
                {t('yes')}
              </Button>
            </DialogFooter>
        </DialogContent>
      </Dialog>

      <div id="datepicker-portal" style={{ position: 'relative', zIndex: 9999 }} />

      <style>{`
        @media (max-width: 639px) {
          .custom-mobile-scrollbar {
            scrollbar-width: auto;
            scrollbar-color: #0c612c;
          }
          .custom-mobile-scrollbar::-webkit-scrollbar {
            width: 12px;
            background: #f0f0f0;
          }
          .custom-mobile-scrollbar::-webkit-scrollbar-thumb {
            background: #0c612c;
            border-radius: 10px;
            min-height: 10px;
          }
          .custom-mobile-scrollbar::-webkit-scrollbar-track {
            background: #f0f0f0;
            border-radius: 10px;
          }
        }
      `}</style>
    </div>
  );
};